/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import CustomCheckbox from '../../../../../../generic/CustomCheckbox';
import CustomTextarea from '../../../../../../generic/CustomTextarea';
import './Risks.scss';
import NotificationAlert from '../../../../../../generic/NotificationAlert';

const RisksItem = ({ register, itemKey, itemTitle, itemDefaultValues, itemErrors, disabled }) => {
  const translate = useIntl();
  const [{ rating, description }] = useState(itemDefaultValues);
  const [warningChecker, setWarnigChecker] = useState(false);
  const [statusAlert, setStatusAlert] = useState({ mitigationsMeasuresIntegrated: false });
  const changeStatusAlert = (type, value) => {
    const isWarning = value.length < 5;
    return setStatusAlert({ ...statusAlert, [type]: isWarning });
  };
  const [radioButtonOptions] = useState([
    { value: '0', label: translate.formatMessage({ id: 'carbon.form.notApplicable' }) },
    { value: '1', label: translate.formatMessage({ id: 'carbon.form.lowProbability' }) },
    { value: '2', label: translate.formatMessage({ id: 'carbon.form.mediumProbability' }) },
    { value: '3', label: translate.formatMessage({ id: 'carbon.form.highProbability' }) },
  ]);
  const warning = statusAlert?.mitigationsMeasuresIntegrated && warningChecker;
  return (
    <div className="risks-item">
      <p className="risks-item__title form-field-title">{itemTitle}</p>
      <div className="risks-item__body">
        <div className="risks-item__radio">
          {radioButtonOptions.map(({ value, label }) => (
            <CustomCheckbox
              type="radio"
              value={value}
              title={label}
              customClass="radio-button-wrapper"
              name={`${itemKey}.rating`}
              defaultChecked={value === rating}
              register={register({ required: true })}
              error={itemErrors?.rating}
              disabled={disabled}
              onChange={(e) => setWarnigChecker(!+e.target.value)}
              key={label}
            />
          ))}
        </div>
        <div className="risks-item__textarea">
          <CustomTextarea
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.mitigationsMeasuresIntegrated' })}
            classTextarea={`textarea-field${warning ? ' warning' : ''}`}
            name={`${itemKey}.description`}
            value={description}
            register={register({ required: true })}
            error={itemErrors?.description}
            disabled={disabled}
            change={(e) => {
              changeStatusAlert('mitigationsMeasuresIntegrated', e.target.value);
            }}
          />
          {warning && <NotificationAlert text={translate.formatMessage({ id: 'carbon.placeholder.mitigationsMeasuresIntegrated.warning' })} />}
        </div>
      </div>
    </div>
  );
};

const RisksGroup = ({ register, groupKey, groupTitle, groupItems, groupDefaultValues, groupErrors, disabled }) => (
  <div className="risks-group__wrapper">
    <p className="risks-group__title">{groupTitle}</p>
    <div className="risks-group">
      {groupItems.map(({ itemKey, itemTitle }) => (
        <div className="risks-group__item" key={itemKey}>
          <RisksItem
            register={register}
            itemKey={`${groupKey}.${itemKey}`}
            itemTitle={itemTitle}
            itemDefaultValues={groupDefaultValues[itemKey]}
            itemErrors={groupErrors?.[itemKey]}
            disabled={disabled}
          />
        </div>
      ))}
    </div>
  </div>
);

const Risks = ({ defaultValues, tabKey, isActiveTab, disabled }) => {
  const translate = useIntl();
  const { register, errors } = useFormContext();
  const { [tabKey]: formErrors } = errors;

  const [naturalRisks] = useState([
    { itemKey: 'fire', itemTitle: translate.formatMessage({ id: 'carbon.form.fire' }) },
    { itemKey: 'pestsAndDiseases', itemTitle: translate.formatMessage({ id: 'carbon.form.pestsAndDiseases' }) },
    { itemKey: 'extremeEvents', itemTitle: translate.formatMessage({ id: 'carbon.form.extremeEvents' }) },
    { itemKey: 'extremeDroughts', itemTitle: translate.formatMessage({ id: 'carbon.form.extremeDroughts' }) },
    { itemKey: 'animalEncroachment', itemTitle: translate.formatMessage({ id: 'carbon.form.animalEncroachment' }) },
  ]);

  const [socialPoliticalRisks] = useState([
    { itemKey: 'disputeOverLandTenureOrOwnership', itemTitle: translate.formatMessage({ id: 'carbon.form.disputeOverLandTenureOrOwnership' }) },
    { itemKey: 'politicalAndSocialInstability', itemTitle: translate.formatMessage({ id: 'carbon.form.politicalAndSocialInstability' }) },
    { itemKey: 'exploitationOfNaturalResources', itemTitle: translate.formatMessage({ id: 'carbon.form.exploitationOfNaturalResources' }) },
    { itemKey: 'lackOfCommunitySupport', itemTitle: translate.formatMessage({ id: 'carbon.form.lackOfCommunitySupport' }) },
  ]);

  const [projectManagementRisks] = useState([
    { itemKey: 'insufficientTechnicalCapacities', itemTitle: translate.formatMessage({ id: 'carbon.form.insufficientTechnicalCapacities' }) },
    { itemKey: 'lackOfEquipmentAndMaterial', itemTitle: translate.formatMessage({ id: 'carbon.form.lackOfEquipmentAndMaterial' }) },
    { itemKey: 'lackOfProjectManagement', itemTitle: translate.formatMessage({ id: 'carbon.form.lackOfProjectManagement' }) },
    { itemKey: 'relianceOnKeyEmployees', itemTitle: translate.formatMessage({ id: 'carbon.form.relianceOnKeyEmployees' }) },
  ]);

  const [financialRisks] = useState([
    { itemKey: 'lackOfSufficientFunding', itemTitle: translate.formatMessage({ id: 'carbon.form.lackOfSufficientFunding' }) },
  ]);

  const [risksGroups] = useState([
    {
      groupKey: 'natural',
      groupTitle: translate.formatMessage({ id: 'carbon.form.naturalRisks' }),
      groupItems: naturalRisks,
    },
    {
      groupKey: 'socialAndPolitical',
      groupTitle: translate.formatMessage({ id: 'carbon.form.socialAndPoliticalRisks' }),
      groupItems: socialPoliticalRisks,
    },
    {
      groupKey: 'projectManagement',
      groupTitle: translate.formatMessage({ id: 'carbon.form.projectManagementRisks' }),
      groupItems: projectManagementRisks,
    },
    {
      groupKey: 'financial',
      groupTitle: translate.formatMessage({ id: 'carbon.form.financialRisks' }),
      groupItems: financialRisks,
    },
  ]);

  return (
    <div className="risks__wrapper" style={{ display: isActiveTab ? 'block' : 'none' }}>
      <ul className="risks__legend" aria-label={translate.formatMessage({ id: 'carbon.form.pleaseEvaluateTheRisks' })}>
        <li>
          <FormattedMessage
            id="carbon.form.notApplicableDescription"
            values={{ b: (chunks) => <b>{chunks}</b> }}
          />
        </li>
        <li>
          <FormattedMessage
            id="carbon.form.lowProbabilityDescription"
            values={{ b: (chunks) => <b>{chunks}</b> }}
          />
        </li>
        <li>
          <FormattedMessage
            id="carbon.form.mediumProbabilityDescription"
            values={{ b: (chunks) => <b>{chunks}</b> }}
          />
        </li>
        <li>
          <FormattedMessage
            id="carbon.form.highProbabilityDescription"
            values={{ b: (chunks) => <b>{chunks}</b> }}
          />
        </li>
      </ul>
      <div className="risks">
        {risksGroups.map(({ groupTitle, groupKey, groupItems }) => (
          <div className="risks__group" key={groupKey}>
            <RisksGroup
              register={register}
              groupKey={`${tabKey}.${groupKey}`}
              groupTitle={groupTitle}
              groupItems={groupItems}
              groupDefaultValues={defaultValues[groupKey]}
              groupErrors={formErrors?.[groupKey]}
              disabled={disabled}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Risks;
